import { yupResolver } from "@hookform/resolvers/yup";
import { useDisclosure } from "@nextui-org/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { deleteFetcher, getFetcher, postFetcher, putFetcher, BASE_URL } from "../../services/api";
import { dirtyInput, isResponseErrorObject } from "../../services/helpers";
import useSWR from "swr";
import { companyValidation } from "../../services/validation/company.validation";
import useAuth from "../useAuth";
import useBadRequestForm from "../useBadRequestForm";
import Cookies from "js-cookie";

export default function useCompany({ mutate, selectedCompanyId, isPengelola }) {
  const { isSuperAdmin, user } = useAuth();
  const { formErrorMessage, setFormErrorMessage, isResponseErrorObject } = useBadRequestForm();
  const { isOpen: isOpenModalForm, onOpen: onOpenModalForm, onOpenChange: onOpenChangeModalForm, onClose: onCloseModalForm } = useDisclosure();
  const { isOpen: isOpenModalAlert, onOpenChange: onOpenChangeModalAlert } = useDisclosure();
  const [editId, setEditId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [suratFile, setSuratFile] = useState(null); // State for file
  const [existingFilePath, setExistingFilePath] = useState(null); // State for existing file path
  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(companyValidation(isSuperAdmin)) });
  const { data: province } = useSWR("/api/location/provinces", getFetcher);
  const provinceId = watch("provinceId");
  const regencyId = watch("regencyId");
  const districtId = watch("districtId");

  const { data: regency } = useSWR(provinceId ? `/api/location/cities?provinceId=${provinceId}` : null, getFetcher, { revalidateOnFocus: false });

  const { data: district } = useSWR(regencyId ? `/api/location/districts?regencyId=${regencyId}` : null, getFetcher, { revalidateOnFocus: false });

  const { data: village } = useSWR(districtId ? `/api/location/villages?districtId=${districtId}` : null, getFetcher, { revalidateOnFocus: false });
  const { data: kbli } = useSWR("/api/data-master/kbli", getFetcher);

  function onClickEdit(item) {
    setEditId(item.id);
    setIsEdit(true);
    reset({
      id: item.id,
      name: item.name,
      penanggungJawab: item.penanggungJawab,
      tipePerusahaan: item.tipePerusahaan,
      skalaPerusahaan: item.skalaPerusahaan,
      alamatKantor: item.alamatKantor,
      telpKantor: item.telpKantor,
      faxKantor: item.faxKantor,
      emailKantor: item.emailKantor,
      npwp: item.npwp,
      idCompanySimpel: item.idCompanySimpel,
      idPerusahaanInduk: item.idPerusahaanInduk,
      bidangUsaha: item.kodeKBLIBidangUsaha || [],
      jenisPelakuUsaha: item.jenisPelakuUsaha ?? "",
      proper: item.proper ?? "",
      nomorInduk: item.nomorInduk,
      isTidakMelakukanPengelolaanB3: item.isTidakMelakukanPengelolaanB3 ?? false,
      companyLokasiKegiatan: item.companyLokasiKegiatan?.map((lokasi) => ({
        id: lokasi.id,
        namaLokasiKegiatan: lokasi.namaLokasiKegiatan,
        alamatLokasiKegiatan: lokasi.alamatLokasiKegiatan,
        latitude: lokasi.latitude,
        longitude: lokasi.longitude,
        provinceId: lokasi.provinceId ?? "",
        regencyId: lokasi.regencyId ?? "",
        districtId: lokasi.districtId ?? "",
        villageId: lokasi.villageId ?? "",
      })) || [{ namaLokasiKegiatan: "", alamatLokasiKegiatan: "", latitude: null, longitude: null, provinceId: "", regencyId: "", districtId: "", villageId: "" }],

      latitude: item.latitude,
      longitude: item.longitude,
      provinceId: item.provinceId ?? "0",
      regencyId: item.regencyId ?? "0",
      districtId: item.districtId ?? "0",
      villageId: item.villageId ?? "0",
    });

    // Set existing file path if exists
    if (item.suratTidakMelakukanPengelolaanB3) {
      setExistingFilePath(item.suratTidakMelakukanPengelolaanB3);
    } else {
      setExistingFilePath(null);
    }

    // Use onOpenModalForm to explicitly open the modal, not toggle
    onOpenModalForm();
  }

  function onCloseForm() {
    setEditId(null);
    setIsEdit(false);
    setSuratFile(null); // Clear file state
    setExistingFilePath(null); // Clear existing file path
    reset({
      id: "",
      name: "",
      penanggungJawab: "",
      tipePerusahaan: [],
      skalaPerusahaan: null,
      alamatKantor: "",
      telpKantor: "",
      faxKantor: "",
      emailKantor: "",
      npwp: "",
      idCompanySimpel: "",
      idPerusahaanInduk: "",
      bidangUsaha: [],
      jenisPelakuUsaha: "",
      proper: "",
      nomorInduk: "",
      isTidakMelakukanPengelolaanB3: false,
      companyLokasiKegiatan: [],
      latitude: null,
      longitude: null,
      provinceId: "",
      regencyId: "",
      districtId: "",
      villageId: "",
    });
    onCloseModalForm();

    // Reload hanya untuk first-time update setelah pilih perusahaan dengan NPWP
    // Untuk edit biasa, cukup refresh data dengan mutate
    if (selectedCompanyId) {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  }

  function onClickDelete(id) {
    setEditId(id);
    onOpenChangeModalAlert();
  }

  async function onSubmitDelete() {
    try {
      await deleteFetcher("/api/company", editId);
      mutate();
      toast.success("Perusahaan berhasil dihapus!");
    } catch (error) {
      toast.error("Gagal hapus perusahaan!");
    }
  }

  async function onSubmitForm(data, suratFile) {
    setFormErrorMessage(null);

    // Validate file requirement for PENGELOLA role
    const isPengelolaRole = !isSuperAdmin && user?.roles?.some((role) => role.role === "PENGELOLA");
    if (isPengelolaRole && data.isTidakMelakukanPengelolaanB3 && !suratFile && !existingFilePath) {
      toast.error("File surat pernyataan wajib diupload untuk role Pengelola");
      return;
    }

    try {
      // Set empty string defaults for required database fields
      data.penanggungJawab = data.penanggungJawab || "";
      data.alamatKantor = data.alamatKantor || "";
      data.telpKantor = data.telpKantor || "";
      data.tipePerusahaan = data.tipePerusahaan || [];

      // data.bidangUsaha contains array of kode: ["11050", "46654"]
      // Save array of kode to kodeKBLIBidangUsaha
      data.kodeKBLIBidangUsaha = data.bidangUsaha || [];

      // Transform bidangUsaha to semicolon-separated judul string
      if (data.bidangUsaha && data.bidangUsaha.length > 0) {
        const judulArray = data.bidangUsaha.map((kode) => {
          const kbliItem = kbli?.find((item) => item.kode === kode);
          return kbliItem ? kbliItem.judul : kode;
        });
        data.bidangUsaha = judulArray.join("; ");
      } else {
        data.bidangUsaha = "";
      }

      data.nib = data.nomorInduk;

      // fix bug lat-long
      if (data.latitude) {
        data.latitude = parseFloat(data.latitude);
      }
      if (data.longitude) {
        data.longitude = parseFloat(data.longitude);
      }

      if (data.companyLokasiKegiatan && data.companyLokasiKegiatan.length > 0) {
        data.companyLokasiKegiatan = data.companyLokasiKegiatan.map((lokasi) => ({
          ...lokasi,
          latitude: lokasi.latitude ? parseFloat(lokasi.latitude) : null,
          longitude: lokasi.longitude ? parseFloat(lokasi.longitude) : null,
        }));
      }      

      // Create FormData
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));
      if (suratFile) {
        formData.append("suratFile", suratFile); // Backend expects 'suratFile' field name
      }

      if (isEdit) {
        // Use axios directly for FormData
        const token = Cookies.get("accessToken");
        const response = await fetch(`${BASE_URL}/api/company/update-company/${editId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw error;
        }

        toast.success("Perusahaan berhasil diubah!");

        // Reload hanya untuk first-time update setelah pilih perusahaan dengan NPWP
        // Untuk edit biasa, cukup refresh data dengan mutate
        if (selectedCompanyId) {
          // Set flag untuk menampilkan alert update SK registrasi
          localStorage.setItem(`showSkRegistrationAlert_${user?.id}`, "true");

          setTimeout(() => {
            window.location.reload();
          }, 500);
          return; // Don't proceed to close modal, reload will handle everything
        }

        // Edit biasa: refresh data dan tutup modal
        await mutate();
        onCloseForm(); // Tutup modal setelah berhasil
      } else {
        // CREATE mode
        data.userId = isSuperAdmin ? null : user.id;

        // Check if this is manual creation by Pengelola (no selectedCompanyId)
        if (isPengelola && !selectedCompanyId) {
          // Manual creation by Pengelola - use new endpoint with SIMPEL integration
          const simpelToken = Cookies.get("simpelToken");

          if (!simpelToken) {
            toast.error("SIMPEL token tidak ditemukan. Silakan login kembali.");
            return;
          }

          formData.set("data", JSON.stringify(data));
          formData.append("simpelToken", simpelToken);

          const token = Cookies.get("accessToken");
          const response = await fetch(`${BASE_URL}/api/company/create-for-pengelola`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            const error = await response.json();
            throw error;
          }

          toast.success("Perusahaan berhasil ditambah dengan integrasi SIMPEL!");

          // Reload halaman untuk refresh data dan authentication state
          setTimeout(() => {
            window.location.reload();
          }, 500);
          return;
        }

        // Normal creation (Super Admin atau role lain yang bukan Pengelola)
        formData.set("data", JSON.stringify(data));

        const token = Cookies.get("accessToken");
        const response = await fetch(`${BASE_URL}/api/company`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw error;
        }

        toast.success("Perusahaan berhasil ditambah!");

        // Reload halaman untuk refresh data
        setTimeout(() => {
          window.location.reload();
        }, 500);
        return; // Don't proceed to close modal
      }
    } catch (error) {
      console.error("Error:", error);

      // Extract error message
      let errorMessage = "Terjadi kesalahan saat menyimpan data perusahaan";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      // Show toast instead of formErrorMessage for better visibility
      toast.error(errorMessage);
    }
  }

  return {
    modalForm: {
      isOpenModalForm,
      onOpenModalForm,
      onOpenChangeModalForm,
    },
    modalAlert: {
      isOpenModalAlert,
      onOpenChangeModalAlert,
    },
    hookForm: {
      register,
      handleSubmit,
      reset,
      watch,
      control,
      setValue,
      formState: { errors, isSubmitting },
    },
    dataFetch: {
      province,
      regency,
      district,
      village,
      kbli,
    },
    formError: {
      formErrorMessage,
    },
    isEdit,
    onClickEdit,
    onCloseForm,
    onClickDelete,
    onSubmitDelete,
    onSubmitForm,
    suratFile,
    setSuratFile,
    existingFilePath,
  };
}
